import cx from 'classnames';
import { useRef, useLayoutEffect, useEffect } from 'react';
import { Drawer } from 'restaurantAdmin/components/drawer/Drawer';
import styles from './MessagingDrawer.scss';
import typography from '~styles/typography.scss';
import { Button, ButtonVariants } from '@components/button/Button';
import { useForm } from 'react-hook-form';
import { ControlledFormTextareaAutosize } from '@components/formInputs/ControlledFormTextareaAutosize';
import {
  useFetchMessages,
  useMarkMessagesAsRead,
  useMessagingDrawer,
  useSaveMessage,
} from './hooks';
import { getGuest } from './apiHelpers';
import { useQuery } from '@tanstack/react-query';
import { useRestaurant } from 'restaurantAdmin/context/useRestaurant';
import { Spinner } from '@components/Spinner';
import { toPrettyDateTime } from '@shared/utils/dateFormatters';

export const MessagingThread = ({ guestId }: { guestId: string }) => {
  const { control, handleSubmit, reset, watch } = useForm({
    defaultValues: {
      message: '',
    },
  });

  const messageValue = watch('message');
  const isMessageEmpty = !messageValue?.trim();
  const { data: messages, isLoading } = useFetchMessages(guestId);
  const { mutate: saveMessageMutation, isPending } = useSaveMessage(
    guestId,
    reset,
  );
  const { mutate: markMessagesAsRead } = useMarkMessagesAsRead();
  const lastMessageRef = useRef<HTMLLIElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.focus();
    }
  }, []);

  const messagesIds = messages.map((message) => message.id).join(',');
  // biome-ignore lint/correctness/useExhaustiveDependencies: what we did was correct
  useEffect(() => {
    if (messages.length > 0) {
      markMessagesAsRead({
        guestId,
        messages,
      });
    }
  }, [messagesIds]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: what we did was correct
  useLayoutEffect(() => {
    if (messages.length > 0) {
      lastMessageRef.current?.scrollIntoView({ behavior: 'smooth' });
      if (inputRef.current) {
        inputRef.current.focus();
      }
    }
  }, [messagesIds]);

  if (isLoading) {
    return <div className={styles.container}>Loading...</div>;
  }

  return (
    <form
      onSubmit={handleSubmit((data) => saveMessageMutation(data))}
      className={styles.conversationForm}
    >
      <ul
        className={styles.messageThread}
        aria-live="polite"
        data-testid="message-thread"
      >
        {!messages || messages.length === 0 ? (
          <p className={styles.noMessages}>
            No messages yet. Start a conversation by typing a message below.
          </p>
        ) : (
          messages.map((message, index) => (
            <li
              key={message.id}
              ref={index === messages.length - 1 ? lastMessageRef : null}
              className={cx(styles.message, {
                [styles.restaurantMessage]: message.sender === 'restaurant',
                [styles.guestMessage]: message.sender === 'guest',
              })}
            >
              <p className={styles.messageTimestamp}>
                {toPrettyDateTime(new Date(message.createdAt))}
              </p>
              <div className={styles.messageContent}>{message.message}</div>
              {message.sentBy && (
                <div className={styles.messageSender}>
                  <span>Sent by {message.sentBy.fullName}</span>
                </div>
              )}
            </li>
          ))
        )}
      </ul>

      <div className={styles.messageInput}>
        <ControlledFormTextareaAutosize
          control={control}
          name="message"
          label=""
          placeholder="Type your SMS message here..."
          className={styles.messageInputTextarea}
          ariaLabel="Message guest"
          minRows={3}
          maxRows={3}
          inputRef={inputRef}
          rules={{
            validate: (value) =>
              typeof value === 'string' && value.trim().length > 0
                ? true
                : 'Message is required',
          }}
        />
        <Button
          type="submit"
          label="Send SMS"
          variant={ButtonVariants.Primary}
          isDisabled={isPending || isMessageEmpty}
        />
      </div>
    </form>
  );
};

export const GuestMessagingDrawer = ({
  guestId,
  isOpen,
}: {
  guestId: string;
  isOpen: boolean;
}) => {
  const restaurant = useRestaurant();
  const { closeMessagingDrawer } = useMessagingDrawer();

  const handleOpenChange = (open: boolean) => {
    if (!open) {
      closeMessagingDrawer();
    }
  };

  const { data: guest, isError: isGuestFetchError } = useQuery({
    queryKey: ['guest', guestId],
    queryFn: () => getGuest(restaurant.id, guestId!),
    enabled: !!guestId,
  });
  const guestDoesNotHaveAPhoneNumber = !guest?.phone;

  return (
    <Drawer
      modal={false}
      side="right"
      open={isOpen}
      onOpenChange={handleOpenChange}
    >
      {guest ? (
        <div className={styles.container}>
          <header className={styles.header}>
            <h2 className={typography.h4}>
              Message {guest.firstName} {guest.lastName}
            </h2>

            <div>
              <Button
                label="Cancel"
                onClick={closeMessagingDrawer}
                variant={ButtonVariants.Tertiary}
              />
            </div>
          </header>

          {guestDoesNotHaveAPhoneNumber ? (
            <p className={styles.errorMessage}>
              This guest does not have a phone number on record.
            </p>
          ) : (
            <MessagingThread guestId={guest.id} />
          )}
        </div>
      ) : (
        <div className={cx(styles.container, styles.noGuest)}>
          {isGuestFetchError ? (
            <div className={styles.container}>
              <header className={styles.header}>
                <h2 className={typography.h4}>Unknown Guest</h2>

                <div>
                  <Button
                    label="Cancel"
                    onClick={closeMessagingDrawer}
                    variant={ButtonVariants.Tertiary}
                  />
                </div>
              </header>
              <p className={styles.errorMessage}>
                Sorry, we couldn't find that guest.
              </p>
            </div>
          ) : (
            <Spinner className={styles.spinner} />
          )}
        </div>
      )}
    </Drawer>
  );
};
